import { useState, useCallback } from 'react';
import { Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FloatingCard } from '@/components';
import { BOTTOM_MENU_ITEMS } from '@/config';
import { Button } from '@/components/ui/button';
import { QUICK_ACTIONS, QUICK_ACTION_IDS } from '../config';
import type { BottomMenuProps } from '../model';

export function BottomMenu({ 
  activeMenuItem, 
  onMenuItemChange, 
  onSelectWishlist, 
  onSelectWish,
  onFindFriend,
  onSecretSanta,
}: BottomMenuProps): JSX.Element {
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const { t } = useTranslation();

  const handleAddClick = useCallback(() => {
    setIsAddMenuOpen(prev => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsAddMenuOpen(false);
  }, []);

  const handleCardClick = useCallback((cardId: string) => {
    setIsAddMenuOpen(false);
    switch (cardId) {
      case QUICK_ACTION_IDS.WISHLIST:
        onSelectWishlist?.();
        break;
      case QUICK_ACTION_IDS.WISH:
        onSelectWish?.();
        break;
      case QUICK_ACTION_IDS.FRIEND:
        onFindFriend?.();
        break;
      case QUICK_ACTION_IDS.SECRET_SANTA:
        onSecretSanta?.();
        break;
    }
  }, [onSelectWishlist, onSelectWish, onFindFriend, onSecretSanta]);

  return (
    <div>
      {/* Затемнённый бэкдроп */}
      {isAddMenuOpen && (
        <button
          onClick={handleClose}
          className="fixed inset-0 z-40 bg-[var(--overlay)] backdrop-blur-sm"
          aria-label={t('navigation:bottomMenu.closeButtonAria')}
        />
      )}

      {/* Сетка быстрых действий 2×2 */}
      {isAddMenuOpen && (
        <div className="fixed bottom-[110px] left-4 right-4 z-40">
          <div className="grid grid-cols-2 gap-2">
            {QUICK_ACTIONS.map((action) => (
              <FloatingCard
                key={action.id}
                icon={action.icon}
                title={t(action.title)}
                onClick={() => handleCardClick(action.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Нижнее меню навигации */}
      <div className="fixed bottom-0 left-0 right-0 z-50">
        <div className="relative w-full h-[98px]">
          {/* Центральная кнопка */}
          <div className="absolute left-1/2 -translate-x-1/2 bottom-[36px] z-10">
            <Button 
              onClick={handleAddClick}
              variant="default"
              size="icon"
              className="active:scale-95 transition-transform rounded-full"
              style={{
                backgroundColor: 'var(--color-accent)',
                width: '60px',
                height: '60px',
              }}
              aria-label={isAddMenuOpen 
                ? t('navigation:bottomMenu.closeButtonAria') 
                : t('navigation:navigation.add')
              }
            >
              {isAddMenuOpen 
                ? <X color="white" strokeWidth={2.5} />
                : <Plus color="white" strokeWidth={2.5} />
              }
            </Button>
          </div>

          {/* Меню с выемкой (SVG) */}
          <div className="absolute bottom-0 left-0 right-0 h-[68px]">
            <svg 
              className="absolute inset-0 w-full h-full drop-shadow-[0px_-2px_12px_rgba(0,0,0,0.08)]" 
              viewBox="0 0 600 68"
              fill="none"
              preserveAspectRatio="xMidYMid slice"
            >
              <path
                d="M 0,0
                   L 252.5,0
                   Q 257.5,0 260.5,5
                   C 267.5,20 274.5,40 300,40
                   C 325.5,40 332.5,20 339.5,5
                   Q 342.5,0 347.5,0
                   L 600,0
                   L 600,68
                   L 0,68
                   Z"
                fill="var(--card)"
                stroke="none"
              />
            </svg>
            
            {/* Иконки меню */}
            <div className="relative h-full flex items-center justify-around px-4">
              {BOTTOM_MENU_ITEMS.map((item) => {
                if (item.isCenter) {
                  // Пустое место для центральной кнопки
                  return <div key={item.id} className="w-[64px]" />;
                }

                const Icon = item.icon;
                const isActive = activeMenuItem === item.id;
                
                return (
                  <Button
                    key={item.id}
                    onClick={() => onMenuItemChange(item.id)}
                    variant="ghost"
                    size="icon-lg"
                    className={`flex flex-col items-center gap-1 active:scale-90 transition-transform ${
                      isActive ? 'text-primary' : 'text-[var(--muted-icon)]'
                    }`}
                    aria-label={t(item.labelKey)}
                  >
                    <span
                      style={isActive ? {
                        filter: 'drop-shadow(0 6px 3px rgba(95, 51, 225, 0.35))',
                      } : undefined}
                    >
                      <Icon className="size-6" />
                    </span>
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}